import React from 'react';
import Icon from '@/components/ui/Icon';
import cn from '@/utils/cn';
import { COLORS, glyphFocusRing } from '@/components/ui/ui.constants';

export type CaretSize = 12 | 24;

interface CaretButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  expanded: boolean;
  size?: CaretSize;
}

// The expand/collapse caret shared by every filter panel: a section header's 24px caret and the
// 12px one on a row inside it. Both point right when collapsed and down when expanded, in
// `very-dark-grey`, with the project's purple focus ring on the glyph rather than the button.
//
// AIDEV-NOTE: forwardRef and the prop spread are load-bearing — Radix's `Collapsible.Trigger
// asChild` clones this and needs to hand it a ref plus aria-expanded/onClick.
//
// AIDEV-NOTE: WCAG 2.5.8 — at 12px the button stays 1rem wide and an `after:` overlay carries the
// 24x24 target, because StandardRow's INDENT_PX/CHEVRON_PX indent maths is built off that width.
// `z-10` is load-bearing: the sibling checkbox has its own 24x24 overlay and, being later in the
// DOM, would otherwise win the strip between them.
const CaretButton = React.forwardRef<HTMLButtonElement, CaretButtonProps>(({
  className, expanded, size = 24, ...rest
}, ref) => (
  <button
    ref={ref}
    className={cn(
      glyphFocusRing,
      'tw-inline-flex tw-items-center tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer',
      size === 24
        ? 'tw-h-6 tw-w-6 tw-justify-center tw-flex-shrink-0'
        : `tw-relative tw-z-10 tw-h-[length:1lh] tw-leading-[inherit] tw-flex-shrink-0 tw-w-4
           after:tw-content-[''] after:tw-absolute after:tw-left-1/2 after:tw-top-1/2
           after:tw--translate-x-1/2 after:tw--translate-y-1/2 after:tw-h-6 after:tw-w-6`,
      className,
    )}
    type="button"
    {...rest}
  >
    <Icon
      fill={COLORS['very-dark-grey']}
      icon={expanded ? 'ChevronDown' : 'ChevronRight'}
      size={size}
    />
  </button>
));

CaretButton.displayName = 'CaretButton';

export default CaretButton;
